import BrainSearchMessageExternalSource from "@/brain-search/internal/BrainSearchMessageExternalSource"
import BrainSearchMessageInternalSource, {
  BRAIN_SEARCH_SOURCE_MIN_WIDTH,
} from "@/brain-search/internal/BrainSearchMessageInternalSource"
import {
  BrainSearchMessageSourceListFragment$data,
  BrainSearchMessageSourceListFragment$key,
} from "@/brain-search/internal/__generated__/BrainSearchMessageSourceListFragment.graphql"
import { BrainSearchMessageSourceListQuery } from "@/brain-search/internal/__generated__/BrainSearchMessageSourceListQuery.graphql"
import { BrainSearchMessageSourceListRefetchQuery } from "@/brain-search/internal/__generated__/BrainSearchMessageSourceListRefetchQuery.graphql"
import CommunityBadge from "@/community/CommunityBadge"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { GlobalID, NodeFromConnection } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoButton, DiscoIcon, DiscoSection, DiscoText } from "@disco-ui"
import { useTheme } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { range } from "@utils/array/arrayUtils"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import { TestIDProps } from "@utils/typeUtils"
import { getFaviconUrl } from "@utils/url/urlUtils"
import classNames from "classnames"
import { useState } from "react"
import {
  graphql,
  useLazyLoadQuery,
  useRefetchableFragment,
  useSubscribeToInvalidationState,
} from "react-relay"

interface Props extends TestIDProps {
  brainSearchMessageId: GlobalID
}

type Source =
  | NodeFromConnection<BrainSearchMessageSourceListFragment$data["sources"]>
  | NodeFromConnection<BrainSearchMessageSourceListFragment$data["externalSources"]>

function BrainSearchMessageSourceList({ brainSearchMessageId }: Props) {
  const classes = useStyles()
  const [viewMore, setViewMore] = useState(false)
  const isMobile = useIsMobile()
  const activeOrganization = useActiveOrganization()!
  const drawer = useGlobalDrawer("brainSearch")
  const isSidePanel = Boolean(drawer.params.drawerAsk)

  const { node } = useLazyLoadQuery<BrainSearchMessageSourceListQuery>(
    graphql`
      query BrainSearchMessageSourceListQuery($id: ID!) {
        node(id: $id) {
          __typename
          id
          ...BrainSearchMessageSourceListFragment
        }
      }
    `,
    { id: brainSearchMessageId },
    { fetchPolicy: "network-only" }
  )

  const [message, refetch] = useRefetchableFragment<
    BrainSearchMessageSourceListRefetchQuery,
    BrainSearchMessageSourceListFragment$key
  >(
    graphql`
      fragment BrainSearchMessageSourceListFragment on BrainSearchMessage
      @refetchable(queryName: "BrainSearchMessageSourceListRefetchQuery") {
        sources {
          totalCount
          edges {
            node {
              id
              __typename
              ...BrainSearchMessageInternalSourceFragment
            }
          }
        }
        externalSources {
          totalCount
          edges {
            node {
              id
              url
              __typename
              ...BrainSearchMessageExternalSourceFragment
            }
          }
        }
      }
    `,
    node
  )

  const externalSources = Relay.connectionToArray(message?.externalSources)
  const internalSources = Relay.connectionToArray(message?.sources)
  const sources = [...internalSources, ...externalSources]

  useSubscribeToInvalidationState([brainSearchMessageId], () => {
    refetch({ id: brainSearchMessageId }, { fetchPolicy: "network-only" })
  })

  if (!sources?.length) return null

  return (
    <div className={classes.sources}>
      <div className={classes.sourcesTag}>
        <DiscoText variant={"heading-xs-700"}>{"Sources"}</DiscoText>
      </div>

      {renderSourcesList()}
    </div>
  )

  function renderSourcesList() {
    // On mobile (not including side panel view), show up to 3 icons in a button that
    // reveals all sources when clicked
    if (isMobile) {
      const icons = (
        <div className={classes.sourceIcons}>
          {sources
            .slice(0, 6)
            .map((source) =>
              source.__typename === "BrainSearchMessageExternalSource" ? (
                <img
                  key={source.id}
                  alt={""}
                  src={getFaviconUrl(source.url, 16)}
                  className={classes.sourceIcon}
                />
              ) : (
                <CommunityBadge
                  key={source.id}
                  organizationKey={activeOrganization}
                  size={16}
                  classes={{ badge: classes.badge }}
                />
              )
            )}
        </div>
      )
      return (
        <DiscoSection className={classes.mobileSourcesSection} padding={0} border>
          <DiscoButton
            onClick={toggleViewMore}
            className={classNames(classes.textBtn, viewMore && classes.textBtnExpanded)}
            variant={"text"}
            color={"grey"}
          >
            <div className={classes.textBtnAdornment}>
              <DiscoIcon icon={"file"} width={16} height={16} />
              <DiscoText variant={"body-xs-700"}>{"Sources"}</DiscoText>
            </div>
            <div className={classes.textBtnAdornment}>
              {icons}
              <DiscoIcon
                icon={viewMore ? "chevron" : "chevron-down"}
                width={16}
                height={16}
              />
            </div>
          </DiscoButton>
          {viewMore && (
            <div className={classNames(classes.sourcesList, classes.mobileSourcesList)}>
              {renderSourceCards(sources)}
            </div>
          )}
        </DiscoSection>
      )
    }

    // On desktop (including side panel view), show the first few sources and hide the
    // rest behind a View More button
    const initialShown = isSidePanel ? 2 : 3
    const initialSources = sources.slice(0, initialShown)
    const moreSources = sources.slice(initialShown)
    return (
      <>
        <div className={classes.sourcesList}>{renderSourceCards(initialSources)}</div>
        {moreSources.length > 0 && (
          <>
            <div
              className={classNames(
                classes.sourcesList,
                classes.moreSourcesContainer,
                viewMore && classes.expanded
              )}
            >
              {renderSourceCards(moreSources)}
            </div>
            <DiscoButton
              onClick={toggleViewMore}
              className={classes.viewMoreButton}
              color={"grey"}
              size={"small"}
              variant={"text"}
            >
              <DiscoText variant={"body-sm-600"} color={"text.secondary"}>
                {viewMore ? "View less" : `View ${moreSources.length} more`}
              </DiscoText>
            </DiscoButton>
          </>
        )}
      </>
    )
  }

  function renderSourceCards(srcs: Source[]) {
    return srcs.map((source) =>
      source.__typename === "BrainSearchMessageExternalSource" ? (
        <BrainSearchMessageExternalSource
          key={source.id}
          brainSearchMessageExternalSourceKey={source}
        />
      ) : (
        <BrainSearchMessageInternalSource
          key={source.id}
          brainSearchMessageSourceKey={source}
        />
      )
    )
  }

  function toggleViewMore() {
    setViewMore((prev) => !prev)
  }
}

const useStyles = makeUseStyles((theme) => ({
  sources: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
  },
  mobileSourcesSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  sourcesTag: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  sourcesList: {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fill, minmax(${BRAIN_SEARCH_SOURCE_MIN_WIDTH}, 1fr))`,
    gridAutoRows: "1fr",
    gap: theme.spacing(1.5),
  },
  mobileSourcesList: {
    padding: theme.spacing(0, 1.5, 1.5),
  },
  sourceIcon: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    background:
      "radial-gradient(100% 100% at 50% 0%, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%), var(--Colors-blue-600, #4D47DC)",
  },
  textBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 1.5),
    borderRadius: theme.measure.borderRadius.big,
  },
  textBtnExpanded: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: theme.palette.constants.borderSmall,
  },
  textBtnAdornment: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),

    "& > svg": {
      color: theme.palette.groovy.neutral[700],
    },
  },
  badge: {
    borderRadius: "50%",
  },
  sourceIcons: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  viewMoreButton: {
    alignSelf: "flex-start",
    backgroundColor: "transparent",
    padding: theme.spacing(1),
    color: theme.palette.groovy.neutral[500],
  },
  moreSourcesContainer: {
    maxHeight: 0,
    opacity: 0,
    overflow: "hidden",
    transition:
      "max-height 0.5s ease-in-out, opacity 0.5s ease-in-out, margin 0.5s ease-in-out",
    marginBottom: theme.spacing(-1.5),
  },
  expanded: {
    maxHeight: "1500px",
    opacity: 1,
    marginBottom: 0,
  },
}))

function BrainSearchMessageSourceListSkeleton() {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useIsMobile()
  const drawer = useGlobalDrawer("brainSearch")

  return (
    <div className={classes.sources}>
      <div className={classes.sourcesTag}>
        <DiscoText variant={"heading-xs-700"}>{"Sources"}</DiscoText>
      </div>
      {renderSourcesList()}
    </div>
  )

  function renderSourcesList() {
    if (isMobile) {
      return <Skeleton variant={"rect"} className={classes.textBtn} height={40} />
    }
    const isSidePanel = Boolean(drawer.params.drawerAsk)
    return (
      <>
        <div className={classes.sourcesList}>
          {range(isSidePanel ? 2 : 3).map((i) => (
            <Skeleton
              key={i}
              variant={"rect"}
              height={96}
              style={{ borderRadius: theme.measure.borderRadius.big }}
            />
          ))}
        </div>
        <Skeleton variant={"rect"} className={classes.textBtn} width={100} height={32} />
      </>
    )
  }
}

export default Relay.withSkeleton({
  component: BrainSearchMessageSourceList,
  skeleton: BrainSearchMessageSourceListSkeleton,
})
